import { useEffect, useState } from 'react';

import { Button, FieldLabel, showSnackbar, TextInput } from 'fluxo-ui';
import { BarChartIcon, CalendarIcon, CheckCircleIcon, EditIcon } from 'fluxo-ui/icons';

import analyticsService from '../services/analytics.service';
import authService from '../services/auth.service';
import { useAuthStore } from '../store/authStore';

const ProfilePage: React.FC = () => {
    const { user, setUser, refreshUser } = useAuthStore();
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [name, setName] = useState(user?.name || '');
    const [errors, setErrors] = useState<{ name?: string }>({});
    const [accountStats, setAccountStats] = useState<{ totalExams: number; totalAttempts: number; averageExamScore: number } | null>(null);

    useEffect(() => {
        refreshUser();
    }, [refreshUser]);

    useEffect(() => {
        setName(user?.name || '');
    }, [user?.name]);

    useEffect(() => {
        const fetchStats = async () => {
            if (!user?._id) return;
            try {
                const data: any = await analyticsService.getUserAnalytics(user._id);
                const history: any[] = Array.isArray(data?.attemptHistory) ? data.attemptHistory : [];
                const uniqueExams = new Set(history.map((a) => String(a.examId))).size;
                const scores = history.map((a) => Number(a.score)).filter((s) => !Number.isNaN(s));
                const averageScore = scores.length ? scores.reduce((sum, s) => sum + s, 0) / scores.length : 0;
                setAccountStats({
                    totalExams: uniqueExams,
                    totalAttempts: history.length,
                    averageExamScore: Math.round(averageScore * 10) / 10,
                });
            } catch {
                setAccountStats({ totalExams: 0, totalAttempts: 0, averageExamScore: 0 });
            }
        };
        fetchStats();
    }, [user?._id]);

    const handleSave = async () => {
        const newErrors: { name?: string } = {};

        if (!name.trim()) {
            newErrors.name = 'Name is required';
        } else if (name.trim().length < 2) {
            newErrors.name = 'Name must be at least 2 characters';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setIsSaving(true);
        try {
            const updatedUser = await authService.updateProfile({ name: name.trim() });
            setUser(updatedUser);
            setIsEditing(false);
            showSnackbar('Profile updated successfully', 'Success', { type: 'success' });
        } catch (err: any) {
            showSnackbar(err.message || 'Failed to update profile', 'Error', { type: 'error' });
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        setName(user?.name || '');
        setErrors({});
        setIsEditing(false);
    };

    const initial = (user?.name || user?.email || '?').charAt(0).toUpperCase();

    const statTiles = [
        {
            label: 'Exams Taken',
            value: accountStats ? accountStats.totalExams : '—',
            icon: CalendarIcon,
            chip: 'bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400',
        },
        {
            label: 'Avg. Score',
            value: accountStats ? `${accountStats.averageExamScore}%` : '—',
            icon: BarChartIcon,
            chip: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
        },
        {
            label: 'Total Attempts',
            value: accountStats ? accountStats.totalAttempts : '—',
            icon: CheckCircleIcon,
            chip: 'bg-cyan-50 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400',
        },
    ];

    return (
        <div className="max-w-4xl">
            {/* Header */}
            <div className="mb-8">
                <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white mb-1">Profile</h1>
                <p className="text-slate-600 dark:text-slate-400">Manage your account information</p>
            </div>

            {/* Profile Card */}
            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
                {/* Header with Avatar */}
                <div className="p-6 border-b border-slate-200 dark:border-slate-700">
                    <div className="flex flex-wrap items-center gap-5">
                        <div className="w-20 h-20 rounded-full bg-linear-to-br from-teal-600 to-cyan-600 flex items-center justify-center shadow-sm shrink-0">
                            <span className="text-3xl font-bold text-white">{initial}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                            <h2 className="text-xl font-bold text-slate-900 dark:text-white truncate">{user?.profile?.name || user?.name}</h2>
                            <p className="text-slate-500 dark:text-slate-400 truncate">{user?.email}</p>
                            <span className="inline-block mt-2 px-2.5 py-0.5 text-xs font-medium bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 rounded-full uppercase tracking-wide">
                                {user?.role?.replace(/_/g, ' ')}
                            </span>
                        </div>
                        {!isEditing && (
                            <Button
                                label="Edit Profile"
                                variant="primary"
                                layout="outlined"
                                leftIcon={EditIcon}
                                onClick={() => setIsEditing(true)}
                            />
                        )}
                    </div>
                </div>

                {/* Profile Details */}
                <div className="p-6 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <FieldLabel label="Full Name" required error={errors.name}>
                            <TextInput
                                value={name}
                                onChange={(e) => {
                                    setName(e.value);
                                    if (errors.name) setErrors({ ...errors, name: undefined });
                                }}
                                placeholder="Enter your name"
                                disabled={!isEditing || isSaving}
                                readonly={!isEditing}
                            />
                        </FieldLabel>

                        <FieldLabel label="Email Address">
                            <TextInput value={user?.email || ''} disabled readonly />
                        </FieldLabel>

                        <FieldLabel label="Account Created">
                            <TextInput value={user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'} disabled readonly />
                        </FieldLabel>

                        <FieldLabel label="Email Verified">
                            <div className="flex items-center h-10">
                                <span
                                    className={`px-2.5 py-0.5 text-xs font-medium rounded-full ${
                                        user?.emailVerified
                                            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                                            : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                                    }`}
                                >
                                    {user?.emailVerified ? 'Verified' : 'Not Verified'}
                                </span>
                            </div>
                        </FieldLabel>
                    </div>

                    {/* Action Buttons */}
                    {isEditing && (
                        <div className="flex gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
                            <Button label="Save Changes" variant="primary" onClick={handleSave} isLoading={isSaving} disabled={isSaving} />
                            <Button label="Cancel" variant="default" layout="outlined" onClick={handleCancel} disabled={isSaving} />
                        </div>
                    )}
                </div>
            </div>

            {/* Account Statistics */}
            <div className="mt-6">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Account Statistics</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {statTiles.map((tile, index) => {
                        const Icon = tile.icon;
                        return (
                            <div
                                key={index}
                                className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-md transition-shadow p-5 flex items-center gap-4"
                            >
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${tile.chip}`}>
                                    <Icon className="w-5 h-5" />
                                </div>
                                <div>
                                    <div className="text-2xl font-bold text-slate-900 dark:text-white tabular-nums">{tile.value}</div>
                                    <div className="text-sm text-slate-500 dark:text-slate-400">{tile.label}</div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default ProfilePage;
